/**
 * CR305+CR306: Right panel — recipe details + test runner
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow, format } from "date-fns";
import type { Recipe } from "@/lib/recipes-studio/types";
import { RecipeTestRunner } from "./RecipeTestRunner";

interface RecipeDetailPanelProps {
  recipe: Recipe | null;
}

export function RecipeDetailPanel({ recipe }: RecipeDetailPanelProps) {
  if (!recipe) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Select or create a recipe to get started
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 h-full overflow-auto">
      {/* Recipe Metadata Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Recipe Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground text-xs">ID</span>
              <p className="font-mono text-xs mt-0.5 truncate">{recipe.id}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">Scope</span>
              <p className="mt-0.5">
                <Badge variant="outline">{recipe.recipe_scope}</Badge>
              </p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">Provider</span>
              <p className="mt-0.5">
                <Badge variant="secondary">{recipe.provider}</Badge>
              </p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">Template</span>
              <p className="mt-0.5">
                <Badge variant={recipe.is_template ? "default" : "outline"}>
                  {recipe.is_template ? "Yes" : "No"}
                </Badge>
              </p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">System Template</span>
              <p className="mt-0.5">
                <Badge variant={recipe.is_system_template ? "default" : "outline"}>
                  {recipe.is_system_template ? "Yes" : "No"}
                </Badge>
              </p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">Created</span>
              <p className="text-xs mt-0.5">
                {format(new Date(recipe.created_at), "MMM d, yyyy HH:mm")}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">Last Updated</span>
              <p className="text-xs mt-0.5">
                {formatDistanceToNow(new Date(recipe.updated_at), { addSuffix: true })}
              </p>
            </div>
          </div>

          {recipe.default_start_url && (
            <div>
              <span className="text-muted-foreground text-xs">Start URL</span>
              <p className="text-xs mt-0.5 font-mono truncate">{recipe.default_start_url}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Runner */}
      <RecipeTestRunner recipe={recipe} />
    </div>
  );
}
